<script lang="ts">
    import { beforeNavigate, goto, pushState } from '$app/navigation';
    import { page } from '$app/stores';
    import { setContext } from 'svelte';
    import { get } from 'svelte/store';
    import {
        OverlayManagerContext,
        OVERLAY_MANAGER_CONTEXT_KEY,
        preloadPageData,
        stripOrigin
    } from './utils';
    import OverlayOutlet from './OverlayOutlet.svelte';
    import { browser } from '$app/environment';

    const closeOverlay = () => {
        if (get(page)?.state?.overlay) {
            const { origin } = get(page).state.overlay!;
            pushState(origin!, { overlay: undefined });
        }
    };

    const manager = new OverlayManagerContext(closeOverlay);
    setContext(OVERLAY_MANAGER_CONTEXT_KEY, manager);

    let navigatingTo: string | null = null;

    beforeNavigate(async ({ from, to, cancel, type, delta }) => {
        if (!from || !to || get(page).route.id?.includes('(overlay)')) return;

        // Scenario where overlay is open: check if we need to close it
        if (Boolean(get(page).state.overlay)) {
            if (!to.route.id?.includes('(overlay)')) {
                // this is a forward button navigation
                // the state there is already settled
                if (type === 'popstate' && delta === 1) return;

                const toUrl = stripOrigin(to.url);
                // Are we already navigating here?
                // avoid recursive trigger
                if (navigatingTo === toUrl) return;

                // Scenario where we navigate to a non overlay page
                // while the overlay is open so we need to clear the overlay
                navigatingTo = toUrl;
                cancel();
                // close overlay
                await goto(stripOrigin(to.url), { state: { overlay: undefined } });
                navigatingTo = null;

                //exit logic here
                return;
            }
        }

        // Scenario where overlay is not open: should we open the overlay page?
        if (to?.route.id?.includes(`(overlay)`)) {
            // If it's a popstate but no overlay data, do nothing.
            if (type === 'popstate' && delta !== 0) return;

            // Cancel current navigation. We take over from here to
            // trigger shallow routing of the overlay page
            cancel();

            // Try to get the overlay page data and await it to settele
            try {
                // Get overlay page data
                const data = (await preloadPageData(stripOrigin(to.url))) as Record<string, any>;
                // Await promises of the data as data needs to be serialized
                await Promise.all(
                    Object.entries(data).map(async ([key, value]) => {
                        data[key] = await value;
                    })
                );
                // Grab origin reference either from existing overlay or
                // from the current page
                const origin = get(page).state.overlay?.origin || stripOrigin(from.url);
                // Trigger overlay shallow routing
                pushState(stripOrigin(to.url), {
                    overlay: { data, origin, template: to.route.id }
                });
            } catch (e) {
                // failsafe mechanism since we cancelled navigation
                // cannot use goto() as that will take us here again
                location.assign(to.url.href);
            }
        }
    });
</script>

{#if browser}
    <OverlayOutlet />
{/if}
<slot />
